import {useCompanyTariffGuard} from '../../../modules/company-tariff';
import {ChangeEvent, FC, useContext, useRef, useState} from 'react';
import {useForm, SubmitHandler, Controller} from 'react-hook-form';
import {useSetRecoilState} from 'recoil';
import ReactSelect from 'react-select';
import {YMaps} from '@pbe/react-yandex-maps';
import {toast} from 'react-toastify';

import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import CircularProgress from '@mui/material/CircularProgress';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import PhoneInput from '../../../ui/phone-input/PhoneInput';
import AddIcon from '@mui/icons-material/Add';
import {HomeAddressMap} from './HomeAddressMap';
import BackspaceIcon from '@mui/icons-material/Backspace';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import {useSearchCompanies} from '../../../hooks/useSearchCompanies';
import {useFileUpload} from '../../../hooks/useFileUpload';

import {roles} from '../../../shared/roles.data';

import {ICompany} from '../../../shared/types/company.types';

import {emailRegExp} from '../../../shared/validation/emailRegExp';

import {UserAccountService} from '../../../api/services/user-account.service';

import {userAccountsQueryTrigger} from '../../../store/users/atoms';

import {visuallyHidden} from '@mui/utils';

import {yellow} from '@mui/material/colors';

import {toValidName} from '../../../shared/helpers/toValidName';

import picture from '../../../assets/Subtract.jpg';
import {copyToClipboard} from '../../../shared/helpers/copyToClipBoard';
import {EventBusContext} from '../../../providers/EventBus';
import {passwordRegExp} from '../../../shared/validation/passwordRegExp';
import {TypeAccountRoles} from '../../../shared/types/user-account.types';

interface IAddUserAccount {
  handleClose: () => void;
  loading: boolean;
  setLoading: (arg: boolean) => void;
}

const iconButtonStyle = {
  padding: '5px !important',
};

const requiredStyle = {
  color: '#EB3333',
};

const labelStyle = {
  color: '#00203399',
};

export const AddUserAccount: FC<IAddUserAccount> = ({handleClose, loading, setLoading}) => {
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const {control, handleSubmit, setValue, watch, getValues, clearErrors} = useForm({mode: 'onChange'});
  const {handleSearch, data, searchTerm, searchLoading} = useSearchCompanies();
  const triggerUsersQuery = useSetRecoilState(userAccountsQueryTrigger);
  const {files, fileNames, setFiles} = useFileUpload();
  const ref = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [openMap, setOpenMap] = useState(false);
  const {bus} = useContext(EventBusContext);
  const geoPosWatch = watch('geoPos');
  const homeAddressWatch = watch('homeAddress');

  const [showPassword, setShowPassword] = useState(false);

  const toggleOpenMap = () => {
    setOpenMap(!openMap);
  };

  const clearHomeAddress = () => {
    setValue('geoPos', undefined);
    setValue('homeAddress', '');
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFiles(e);
  };

  const copyValue = copyToClipboard(getValues);

  const onSubmit: SubmitHandler<any> = async (data, e) => {
    const roles = data.roles?.map((v: {label: string; value: string}) => v.value) || [];
    const formData = new FormData();

    try {
      setLoading(true);

      const {data: user} = await UserAccountService.create({
        ...data,
        firstName: toValidName(data.firstName.trim()),
        lastName: toValidName(data.lastName.trim()),
        companyId: data.companyId?.id || null,
        position: data.position || null,
        roles: roles.length ? [TypeAccountRoles.VIEWER, ...roles] : [TypeAccountRoles.VIEWER],
        comments: data.comments ? data.comments.trim() : null,
        isActive: true,
        patronymic: data.patronymic ? toValidName(data.patronymic.trim()) : null,
        homeAddress: data.homeAddress || null,
        homeAddressLatitude: data.geoPos ? data.geoPos[0] : null,
        homeAddressLongitude: data.geoPos ? data.geoPos[1] : null,
      });

      fileNames[0] && formData.append('photo', files[0]);

      fileNames[0] && (await UserAccountService.addUserPhoto(user.id, formData));

      toast.success('Пользователь добавлен', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      triggerUsersQuery((v) => v + 1);

      setTimeout(() => {
        setLoading(false);
        handleClose();
      });
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  return (
    <>
      <form
        ref={ref}
        onSubmit={(event) => {
          event.preventDefault();
          if (checkTariff()) void handleSubmit(onSubmit)(event);
        }}
      >
        <Stack direction="column" spacing={1}>
          <Stack direction="row">
            <Typography variant="h5">Добавить пользователя</Typography>
          </Stack>
          <Stack direction="column" spacing={1} sx={{overflow: 'hidden'}}>
            <Stack direction="row" spacing={2}>
              <Typography sx={labelStyle} variant="subtitle2">
                Фото профиля
              </Typography>
              <button type="button" style={{cursor: 'pointer'}} onClick={() => inputRef?.current?.click()}>
                {fileNames.length ? 'Выбрать другой' : 'Выбрать файл'}
              </button>
              <Tooltip title="Максимальный размер файла: 512KB">
                <ErrorOutlineIcon htmlColor={yellow[900]} />
              </Tooltip>
            </Stack>
            <Stack
              direction="column"
              spacing={2}
              sx={{
                height: '116px',
                border: '1px solid #D4D4D4',
                justifyContent: 'center',
                alignItems: fileNames[0] ? 'normal' : 'center',
                p: '0 16px',
              }}
            >
              <Stack direction="row" sx={{justifyContent: 'center'}}>
                <Avatar
                  sx={{width: 70, height: 70}}
                  variant="rounded"
                  src={fileNames[0] ? URL.createObjectURL(files[0]) : picture}
                  alt="Avatar"
                />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Typography textOverflow="ellipsis" variant="subtitle2" sx={{color: '#00203399'}} noWrap>
                  {fileNames[0] || 'Ничего не выбрано'}
                </Typography>
                <input type="file" ref={inputRef} style={{display: 'none'}} onChange={onChange} accept="image/*" />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Логин
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <ControlledInput
                control={control}
                name="userLogin"
                disabled={loading}
                size="small"
                rules={{
                  required: 'Обязательно для заполнения',
                  pattern: {
                    value: /^[a-zA-Z0-9_]+$/,
                    message: 'Допустимы латинские буквы и цифры, нижнее подчеркивание',
                  },
                }}
              />
            </Stack>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={1}>
              <Stack direction="column" spacing={0.25}>
                <Stack direction="row" spacing={0.25}>
                  <Typography sx={labelStyle} variant="subtitle2">
                    Пароль
                  </Typography>
                  <Typography sx={requiredStyle} variant="subtitle2">
                    *
                  </Typography>
                </Stack>
                <ControlledInput
                  control={control}
                  name="password"
                  disabled={loading}
                  size="small"
                  rules={{
                    required: 'Обязательно для заполнения',
                    pattern: {
                      value: passwordRegExp,
                      message:
                        'От 12 символов с использованием цифр, спец. символов, латиницы, наличием строчных и прописных символов',
                    },
                  }}
                  type={showPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)}>
                          {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <ControlledInput
                control={control}
                name="checkPassword"
                size="small"
                type="password"
                disabled={loading}
                placeholder="Подтвердите пароль"
                rules={{
                  required: true,
                  validate: {
                    isTheSame: (v, values) => {
                      return values.password === v ? true : 'Пароли не совпадают';
                    },
                  },
                }}
              />
            </Stack>
          </Stack>
          <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
            <Stack direction="row" spacing={1}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  ФИО
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <IconButton
                onClick={() => copyValue(['lastName', 'firstName', 'patronymic'])}
                sx={{padding: '0 !important'}}
              >
                <ContentCopyIcon sx={{height: '17px !important', width: '17px !important'}} />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <ControlledInput
                control={control}
                name="lastName"
                size="small"
                disabled={loading}
                placeholder="Фамилия"
                sx={{borderRadius: '0 !important'}}
                rules={{
                  required: 'Обязательно для заполнения',
                }}
              />
              <ControlledInput
                control={control}
                name="firstName"
                size="small"
                placeholder="Имя"
                disabled={loading}
                rules={{
                  required: 'Обязательно для заполнения',
                }}
              />
              <ControlledInput
                control={control}
                placeholder="Отчество (при наличии)"
                name="patronymic"
                size="small"
                disabled={loading}
              />
            </Stack>
          </Stack>
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Email
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <ControlledInput
                control={control}
                name="email"
                size="small"
                disabled={loading}
                rules={{
                  pattern: {
                    value: emailRegExp,
                    message: 'Некорректный email',
                  },
                  required: 'Обязательно для заполнения',
                }}
                InputProps={{
                  endAdornment: (
                    <Stack direction="row">
                      <IconButton onClick={() => copyValue(['email'])} size="small" sx={iconButtonStyle}>
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton size="small" sx={iconButtonStyle}>
                        <EmailIcon fontSize="inherit" htmlColor="#0D99FF" />
                      </IconButton>
                    </Stack>
                  ),
                }}
              />
            </Stack>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Stack direction="row" spacing={0.25}>
                <Typography sx={labelStyle} variant="subtitle2">
                  Номер телефона
                </Typography>
                <Typography sx={requiredStyle} variant="subtitle2">
                  *
                </Typography>
              </Stack>
              <PhoneInput
                control={control}
                name="phoneNumber"
                disabled={loading}
                rules={{
                  required: 'Обязательно для заполнения',
                  pattern: {
                    value: /^(\+7 \([1-9]\d{2}\) \d{3}-\d{2}-\d{2}( #\d+)?$)/,
                    message: 'Некорректный номер или добавочный',
                  },
                }}
                size="small"
                endAdornment={
                  <Stack direction="row" spacing={0.25}>
                    <IconButton onClick={() => copyValue(['phoneNumber'])} size="small" sx={iconButtonStyle}>
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton size="small" sx={iconButtonStyle}>
                      <LocalPhoneIcon fontSize="inherit" htmlColor="#2CC378" />
                    </IconButton>
                  </Stack>
                }
              />
            </Stack>
          </Stack>
          <Stack direction="row" sx={{justifyContent: 'space-between'}}>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Typography sx={labelStyle} variant="subtitle2">
                Должность
              </Typography>
              <ControlledInput control={control} name="position" disabled={loading} size="small" />
            </Stack>
            <Stack direction="column" sx={{width: '48.5%'}} spacing={0.25}>
              <Typography sx={labelStyle} variant="subtitle2">
                Домашний адрес
              </Typography>
              {homeAddressWatch ? (
                <Tooltip title={homeAddressWatch}>
                  <Box>
                    <ControlledInput
                      control={control}
                      name="homeAddress"
                      disabled
                      sx={{
                        '& input:disabled': {
                          '-webkit-text-fill-color': `rgb(0 0 0 / 85%) !important`,
                        },
                      }}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <>
                            {geoPosWatch ? (
                              <>
                                <Tooltip title="Удалить">
                                  <IconButton size="small" onClick={clearHomeAddress} sx={iconButtonStyle}>
                                    <BackspaceIcon color="error" fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Изменить">
                                  <IconButton size="small" onClick={toggleOpenMap} sx={iconButtonStyle}>
                                    <EditIcon color="primary" fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip title="Открыть карту">
                                <IconButton onClick={toggleOpenMap} sx={iconButtonStyle}>
                                  <AddIcon htmlColor="#2CC378" fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        ),
                      }}
                    />
                  </Box>
                </Tooltip>
              ) : (
                <ControlledInput
                  control={control}
                  name="homeAddress"
                  disabled
                  sx={{
                    '& input:disabled': {
                      '-webkit-text-fill-color': `rgb(0 0 0 / 85%) !important`,
                    },
                  }}
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <>
                        {geoPosWatch ? (
                          <>
                            <Tooltip title="Удалить">
                              <IconButton size="small" onClick={clearHomeAddress} sx={iconButtonStyle}>
                                <BackspaceIcon color="error" fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Изменить">
                              <IconButton size="small" onClick={toggleOpenMap} sx={iconButtonStyle}>
                                <EditIcon color="primary" fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Tooltip title="Открыть карту">
                            <IconButton onClick={toggleOpenMap} sx={iconButtonStyle}>
                              <AddIcon htmlColor="#2CC378" fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    ),
                  }}
                />
              )}
              <ControlledInput control={control} name="geoPos" sx={visuallyHidden} />
            </Stack>
          </Stack>
          <Stack direction="column" sx={{width: '100%'}} spacing={0.25}>
            <Typography sx={labelStyle} variant="subtitle2">
              Роли
            </Typography>
            <Controller
              name="roles"
              control={control}
              render={({field: {onChange, value, ref}}) => (
                <ReactSelect
                  options={roles}
                  value={value}
                  isDisabled={loading}
                  onChange={onChange}
                  ref={ref}
                  isMulti
                  placeholder=" "
                />
              )}
            />
          </Stack>
          <Stack direction="column" sx={{width: '100%'}}>
            <Typography sx={labelStyle} variant="subtitle2">
              Название компании
            </Typography>
            <Controller
              control={control}
              name="companyId"
              rules={{
                required: 'Обязательно для заполнения',
              }}
              render={({field: {value}, fieldState: {error}}) => (
                <Autocomplete
                  open={open}
                  disabled={loading}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={() => {
                    setOpen(false);
                  }}
                  onChange={(_, newValue: ICompany) => {
                    setValue('companyId', newValue);

                    if (newValue) {
                      clearErrors('companyId');
                    }
                  }}
                  value={value}
                  getOptionLabel={(option) => `${option.fullCompanyName},   ИНН: ${option.inn}`}
                  inputValue={searchTerm}
                  onInputChange={(_, newInputValue) => {
                    handleSearch(newInputValue);
                  }}
                  options={data}
                  loading={searchLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Название / ИНН / комментарий"
                      size="small"
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Stack>
          <Stack direction="column" spacing={0.25}>
            <Typography sx={labelStyle} variant="subtitle2">
              Комментарий
            </Typography>
            <ControlledInput
              control={control}
              name="comments"
              rules={{
                maxLength: {
                  value: 255,
                  message: 'Максимум 255 символов',
                },
              }}
              disabled={loading}
              size="small"
              rows={4}
              multiline
            />
          </Stack>
        </Stack>
        <Stack direction="row" sx={{justifyContent: 'space-between', mt: 4}}>
          <LoadingButton
            onClick={() => {
              (ref?.current as any).requestSubmit();
            }}
            variant="outlined"
            color="success"
            disabled={tariffLoading}
            loading={loading}
          >
            Добавить
          </LoadingButton>
          <Button onClick={handleClose} variant="outlined" color="error" disabled={loading}>
            Отменить
          </Button>
        </Stack>
      </form>
      {openMap && (
        <YMaps
          query={{
            apikey: process.env.REACT_APP_YANDEX_MAPS_API_KEY,
            lang: 'ru_RU',
          }}
        >
          <HomeAddressMap
            open={openMap}
            handleClose={toggleOpenMap}
            setValue={setValue}
            defaultGeoPos={geoPosWatch}
            defaultHomeAddress={homeAddressWatch}
          />
        </YMaps>
      )}
    </>
  );
};
